import React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import style from './SubscriptionChip.module.scss';

import { translate } from '@chaika/i18n/helper';
import { store } from '@store';

import { SubscriptionCreationPopup } from '../SubscriptionCreationPopup';
import Add from './add.svg';
import Clock from './clock.svg';
import Person from './person.svg';
import { usePatientSubscriptionStatus } from './hooks/usePatientSubscriptionStatus';

interface Props {
    patientId: string;
    hideLabel?: boolean;
}

export const SubscriptionChip = observer(({ patientId, hideLabel }: Props): React.ReactElement<any> | null => {
    const { app } = store;

    const { isLoaded, hasActiveSubscription, hasTrialSubscription, subscriptionDoctorNames, reload } =
        usePatientSubscriptionStatus({ patientId });

    const openSubscriptionCreation = (): void => {
        app.showPopup({
            withoutCloseButton: true,
            description: () => (
                <SubscriptionCreationPopup
                    patientId={patientId}
                    onCreated={reload}
                />
            ),
            onClose: app.showPopup.bind(app, null),
        });
    };

    if (!isLoaded) {
        return null;
    }

    if (!hasActiveSubscription && !hasTrialSubscription) {
        return (
            <button
                type="button"
                className={classnames([style.buttonReset, style.chip, style.addChip])}
                title={translate('addSubscription')}
                onClick={openSubscriptionCreation}
            >
                <Add />
                {!hideLabel && translate('addSubscription')}
            </button>
        );
    }

    const text = hasTrialSubscription
        ? translate('trialSubscriptionShort')
        : formatSubscriptionText(subscriptionDoctorNames);

    return (
        <div
            className={classnames([style.chip, style.infoChip])}
            title={text}
        >
            {hasTrialSubscription ? <Clock /> : <Person />}
            {!hideLabel && text}
        </div>
    );
});

function formatSubscriptionText(doctorNames: string[]): string {
    const [firstDoctorName, ...otherDoctorNames] = doctorNames;

    if (!firstDoctorName) {
        return translate('subscription');
    }

    const otherDoctorsSuffix = otherDoctorNames.length > 0 ? ` + ${otherDoctorNames.length}` : '';

    return `${translate('subscription')} (${firstDoctorName}${otherDoctorsSuffix})`;
}
